import React, { useState, useEffect, useContext } from "react";
import SearchInput from "../search/SearchInput";
import ActionButton from "../../components/Button/ActionButton";
import FieldVisibilityDropdown from "../Filter/FieldVisibilityDropdown";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import ReportExportOptions from "../customReport/ReportExportOptions";
import { useCustomSearchParams } from "../hooks/useCustomSearchParams";
import { SEARCH_PARAMS } from "../../utils/SearchParams";
import AxiosService from "../../utils/AxiosService";
import ExportReportModal from "../Modal/ExportReportModal/ExportReportModal";
import "./PublicationToolbar.scss";
import { KEY_NAME } from "../../utils/KeyName";
import { AppContext } from "../../context/AppContext";

const categoryObj = {
  workCount: "site_all",
  publiclyAccessibleYes: "site_yes",
  publiclyAccessibleUnknown: "site_unknown",
  licenseYes: "reuse_term_yes",
  licenseUnknown: "reuse_term_unknown",
  archiveYes: "archive_yes",
  archiveUnknown: "archive_unknown",
};

const showHideFieldObj = {
  doi: { field: "DOI", label: "DOI" },
  retracted: { field: "Retracted ?", label: "Retracted" },
  title: { field: "Article Title", label: "Article Title" },
  authors: { field: "Author(s)", label: "Authors" },
  publisher: { field: "Publisher", label: "Publisher" },
  journalName: { field: "Journal Name", label: "Journal Name" },
  licenseType: { field: "License Type", label: "License Type" },
  licenseUrl: { field: "License URL", label: "License URL" },
  licenseDate: {
    field: "Reuse License Start Date",
    label: "Reuse License Start Date",
  },
  funders: { field: "Agencies", label: "Agencies" },
  datasetsDli: { field: "Datasets", label: "Datasets" },
  onlinePublicationDate: {
    field: "Online Publication Date",
    label: "Online Publication Date",
  },
  printPublicationDate: {
    field: "Print Publication Date",
    label: "Print Publication Date",
  },
  auditedOnDate: { field: "Last Audited", label: "Last Audited" },
  publiclyAccessibleDate: {
    field: "Publicly Accessible On Publisher Site",
    label: "Verified OA on publisher site",
  },
  createdAtDate: { field: "Added", label: "Added" },
  updatedAtDate: { field: "Updated", label: "Updated" },
  auditSource: { field: "Audit Source", label: "Audit Source" },
  funderIds: { field: "Funder Id", label: "Funder Id" },
  funderRorIds: { field: "Funder RORId", label: "Funder RORId" },
  funderSource: { field: "Funder Source", label: "Funder Source" },
  type: { field: "Type", label: "Type" },
  archivedStatus: { field: "Archived", label: "Archived" },
  grantId: { field: "Grant ID", label: "Grant ID" },
  agencyPortalUrls: {
    field: "Agency Portal Urls",
    label: "Agency Portal Urls",
  },
};

const fieldTypeOption = [
  { label: "All fields", value: "all_fields" },
  { label: "Selected fields", value: "selected_fields" },
];

const PublicationToolbar = ({
  agencyId = "",
  memberId = "",
  selectedFunderFromTable = "",
  publisherCount,
  onSearch,
  onChange,
  handleShowHideFilter,
  tableConfig,
  selectedSearchValue,
}) => {
  const axiosService = new AxiosService();
  const { get, set } = useCustomSearchParams();
  const { entityName, selectedFilterBy } = useContext(AppContext);

  const fields = [
    {
      key: "articleMetadata",
      label: "Article Metadata",
      children: [
        { key: "doi", label: "DOI" },
        { key: "retracted", label: "Retracted" },
        ...(entityName === KEY_NAME.FUNDER
          ? [{ key: "publisher", label: "Publisher" }]
          : []),
        { key: "title", label: "Article Title" },
        { key: "authors", label: "Authors" },
        { key: "journalName", label: "Journal" },
        { key: "type", label: "Type" },
        { key: "grantId", label: "Grant ID" },
        { key: "archivedStatus", label: "Archived Status" },
      ],
    },
    {
      key: "articleLicensing",
      label: "Article Licensing",
      children: [
        { key: "licenseType", label: "License Type" },
        { key: "licenseUrl", label: "License URL" },
        { key: "licenseDate", label: "Reuse License Start Date" },
        { key: "auditSource", label: "Audit Source" },
      ],
    },
    {
      key: "funderMetadata",
      label: "Funder Metadata",
      children: [
        { key: "funders", label: "Funders" },
        { key: "funderIds", label: "Funder ID" },
        { key: "funderRorIds", label: "Funder ROR ID" },
        { key: "agencyPortalUrls", label: "Funder Portal URL" },
        { key: "funderSource", label: "Funder Source" },
      ],
    },
    {
      key: "datasetMetadata",
      label: "Dataset Metadata",
      children: [{ key: "datasetsDli", label: "Dataset URL" }],
    },
    {
      key: "keyDates",
      label: "Key Dates",
      children: [
        { key: "onlinePublicationDate", label: "Online Publication Date" },
        { key: "printPublicationDate", label: "Print Publication Date" },
        { key: "auditedOnDate", label: "Last Audited" },
        {
          key: "publiclyAccessibleDate",
          label: "Open Access On Publisher Site",
        },
        { key: "createdAtDate", label: "Added" },
        { key: "updatedAtDate", label: "Updated" },
      ],
    },
  ];

  // Builds the checkbox state from a set of visible leaf-field keys, whether
  // that set came from the server's default tableConfig or a restored URL.
  const buildVisibility = (fields, visibleKeys) => {
    const state = {};

    fields?.forEach((parent) => {
      let allChildrenTrue = true;

      parent.children?.forEach((child) => {
        const isVisible = visibleKeys.has(child.key);

        state[child.key] = isVisible;

        if (!isVisible) {
          allChildrenTrue = false;
        }
      });

      // Parent true only if ALL children are true
      state[parent.key] = allChildrenTrue;
    });

    return state;
  };

  const visibleKeysFromTableConfig = (tableConfig) =>
    new Set(
      tableConfig
        ?.filter((item) => item.tableDisplay)
        .map((item) => item.field),
    );

  const [visibleFields, setVisibleFields] = useState(() => {
    const urlVisibleFields = get(SEARCH_PARAMS.VISIBLE_FIELDS);
    const visibleKeys = urlVisibleFields
      ? new Set(urlVisibleFields.split(","))
      : visibleKeysFromTableConfig(tableConfig);
    return buildVisibility(fields, visibleKeys);
  });
  const [showModule, setShowModule] = useState(false);
  const [showExportReportMsg, setShowExportReportMsg] = useState(false);
  const [shortName, setShortName] = useState(get(SEARCH_PARAMS.SHORT_NAME));
  const [query, setQuery] = useState({
    publisher: get(SEARCH_PARAMS.SHORT_NAME),
    category: "",
    clickedValue: 0,
    field: fieldTypeOption[0].value,
    columns: "",
  });
  const [resCode, setResCode] = useState(null);

  const handleModalClose = () => {
    setShowExportReportMsg(false);
  };

  const handleExportReport = (queryData) => {
    setQuery((prev) => {
      return { ...prev, ...queryData };
    });
    let reqBody = {};
    if (query.field === "all_fields") {
      const { columns, ...restQuery } = query;
      reqBody = {
        ...restQuery,
        ...queryData,
        searchedDoi: get(SEARCH_PARAMS.ENTITY_DETAILS_SEARCH) || "",
      };
    } else {
      reqBody = {
        ...query,
        ...queryData,
        searchedDoi: get(SEARCH_PARAMS.ENTITY_DETAILS_SEARCH) || "",
      };
    }

    if (false) {
      // if (selectedFunderFromTable) {
      if (reqBody.category === "site_all") {
        reqBody = { ...reqBody, category: "all", id: agencyId };
      } else if (reqBody.category === "site_yes") {
        reqBody = {
          ...reqBody,
          category: "url_accessible",
          subcategory: "yes",
          id: agencyId,
        };
      } else if (reqBody.category === "site_unknown") {
        reqBody = {
          ...reqBody,
          category: "url_accessible",
          subcategory: "remaining",
          id: agencyId,
        };
      } else if (reqBody.category === "reuse_term_yes") {
        reqBody = {
          ...reqBody,
          category: "license",
          subcategory: "acceptable",
          id: agencyId,
        };
      } else if (reqBody.category === "reuse_term_unknown") {
        reqBody = {
          ...reqBody,
          category: "license",
          subcategory: "unknown",
          id: agencyId,
        };
      } else if (reqBody.category === "archive_yes") {
        reqBody = {
          ...reqBody,
          category: "archive",
          subcategory: "acceptable",
          id: agencyId,
        };
      } else if (reqBody.category === "archive_unknown") {
        reqBody = {
          ...reqBody,
          category: "archive",
          subcategory: "unknown",
          id: agencyId,
        };
      } else if (reqBody.category === "agencyPortal") {
        reqBody = {
          ...reqBody,
          category: "portal_doe",
          subcategory: "exist",
          id: agencyId,
        };
      }

      axiosService
        .getPublisherExportReportForAgencies(
          { ...reqBody, agency: selectedFunderFromTable },
          shortName,
        )
        .then((response) => {
          setResCode(response.status);
        })
        .catch((error) => {
          console.error("Error:", error);
          setResCode(error.status || "Network Error");
        })
        .finally(() => {});
    } else {
      switch (entityName) {
        case KEY_NAME.FUNDER:
          // console.log(
          //   "Funder export report request body:",
          //   reqBody,
          //   selectedFilterBy.split(":")[1],
          // );
          axiosService
            .postFunderExportReportForOpenAccessOnPubSite(
              {
                ...reqBody,
                memberId: selectedFilterBy.split(":")[1] || memberId,
              },
              shortName,
            )
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);
              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
          break;
        case KEY_NAME.PUBLISHER:
        case KEY_NAME.INSTITUTION:
          axiosService
            .postPublisherExportReportForOpenAccessOnPubSite(
              {
                ...reqBody,
                agencyId: selectedFilterBy.split(":")[1] || agencyId,
              },
              shortName,
            )
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);
              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
          break;
        default:
          break;
      }
    }
  };

  const extractSelectedFields = (fields, visibilityMap) => {
    const result = {};

    fields.forEach((parent) => {
      if (!visibilityMap[parent.key]) return;

      const selectedChildren = parent.children
        ?.filter((child) => visibilityMap[child.key])
        .map((child) => child.key);

      result[parent.key] = selectedChildren || [];
    });

    return result;
  };

  const handleApply = (updatedVisibility) => {
    setVisibleFields(updatedVisibility);

    const selectedFields = extractSelectedFields(fields, updatedVisibility);
    const trueKeys = Object.keys(updatedVisibility).filter(
      (key) => updatedVisibility[key],
    );
    handleShowHideFilter(trueKeys);
    // handleShowHideFilter(selectedFields);

    // Persist only the leaf field keys (not the group keys also present in
    // trueKeys) so the selection can be restored from a shared/reloaded URL.
    const leafKeys = fields.flatMap((parent) =>
      (parent.children || []).filter((child) => updatedVisibility[child.key]),
    );
    set(
      SEARCH_PARAMS.VISIBLE_FIELDS,
      leafKeys.map((child) => child.key).join(","),
    );
  };

  const handleCancel = () => {
    // reset handled automatically via prop sync
  };

  const handleReportBtn = () => {
    setShowModule(true);
  };

  const handleCloseReport = () => {
    setResCode("");
    setQuery((prev) => ({
      ...prev,
      field: fieldTypeOption[0].value,
    }));
    setShowModule(false);
  };

  useEffect(() => {
    if (publisherCount) {
      setQuery((prev) => ({
        ...prev,
        clickedValue:
          get(SEARCH_PARAMS.ACTIVE_METRICS_CARD) &&
          publisherCount[get(SEARCH_PARAMS.ACTIVE_METRICS_CARD)],
        category: categoryObj?.[get(SEARCH_PARAMS.ACTIVE_METRICS_CARD)],
      }));
    }
  }, [publisherCount?.[get(SEARCH_PARAMS.ACTIVE_METRICS_CARD)]]);

  useEffect(() => {
    if (resCode) {
      setShowExportReportMsg(true);
    }
  }, [resCode]);

  useEffect(() => {
    if (visibleFields) {
      const trueKeys = Object.keys(visibleFields).filter(
        (key) => visibleFields[key] === true,
      );

      const colums = trueKeys
        .filter(
          (item) =>
            ![
              "articleMetadata",
              "articleLicensing",
              "funderMetadata",
              "datasetMetadata",
              "keyDates",
            ].includes(item),
        )
        .map((item) => showHideFieldObj[item])
        .filter(Boolean);

      const index = colums.findIndex((col) => col.field === "Retracted ?");

      const updatedColumns =
        index !== -1
          ? [
              ...colums.slice(0, index + 1),
              {
                field: "Retraction DOI",
                label: "Retraction DOI",
              },
              ...colums.slice(index + 1),
            ]
          : colums;

      // if (colums.some((col) => col.field === "Retracted ?")) {
      //   colums.push({
      //     field: "Retraction DOI",
      //     label: "Retraction DOI",
      //   });
      // }

      setQuery((prev) => ({ ...prev, columns: updatedColumns }));
    }
  }, [visibleFields]);

  return (
    <>
      <div className="row align-items-center gap-2">
        {/* LEFT */}
        <div className="col-12 col-sm-6">
          <SearchInput
            placeholder="Search for a DOI"
            onSearch={onSearch}
            onChange={onChange}
            selectedSearchValue={selectedSearchValue}
          />
        </div>

        {/* CENTER */}
        <div className="col">
          <FieldVisibilityDropdown
            fields={fields}
            value={visibleFields}
            onChange={setVisibleFields}
            onApply={handleApply}
            onCancel={handleCancel}
          />
        </div>

        {/* RIGHT */}
        <div className="col-12 col-sm-2  ps-sm-1">
          <ActionButton
            icon={faEnvelope}
            label="Email Report"
            onClick={handleReportBtn}
          />
        </div>
      </div>
      {showModule && (
        <>
          <div className="modal-backdrop fade show"></div>

          <div className="modal fade show d-block">
            <div className="modal-dialog  mt-5">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-dark">Email Report</h5>
                  <button
                    className="btn-close"
                    onClick={handleCloseReport}
                  ></button>
                </div>

                <div className="modal-body d-flex flex-column">
                  <p>
                    Reports will be sent to the email address entered below.{" "}
                    <br />
                    Extra large reports may be delivered in multiple files by
                    year.
                  </p>
                  <div className="mb-4">
                    <div>Generate Report For</div>
                    <div className="d-flex align-items-center gap-5">
                      {fieldTypeOption.map((item, index) => (
                        <div key={index}>
                          <>
                            <input
                              type="radio"
                              name="fieldType"
                              id={item.value}
                              value={item.value}
                              checked={query.field === item.value}
                              className="me-2 form-check-input"
                              disabled={
                                !Object.values(visibleFields).some(
                                  (value) => value === true,
                                ) && index === 1
                              }
                              onChange={() =>
                                setQuery((prev) => ({
                                  ...prev,
                                  field: item.value,
                                }))
                              }
                            />
                            <label htmlFor={item.value}>{item.label}</label>
                          </>
                        </div>
                      ))}
                    </div>
                  </div>
                  <ReportExportOptions
                    handleExportReport={handleExportReport}
                    showTimeFilter={false}
                    showFileType={false}
                    handleClose={handleCloseReport}
                    width="full"
                  />
                  <button
                    type="button"
                    className="btn btn-light mt-2"
                    onClick={handleCloseReport}
                  >
                    Cancel
                  </button>
                </div>

                {/* <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModule(false)}
                  >
                    Close
                  </button>

                  <button className="btn btn-primary">Save Changes</button>
                </div> */}
              </div>
            </div>
          </div>
        </>
      )}

      {showExportReportMsg && (
        <ExportReportModal resCode={resCode} handleClose={handleModalClose} />
      )}
    </>
  );
};

export default PublicationToolbar;
